import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import QuestionnaireService from "@/services/api/questionnaire";
import type { GetQuestionParams, PostQuestionRequest, QuestionField, QuestionOrderItem } from "@/types/question";

export interface QuestionState {
  questions: QuestionField[];
  totalQuestions: number;
  isFetching: boolean;
  isPosting: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  dependentQuestions: QuestionField[];
  isFetchingDependentQuestions: boolean;
}

const initialState: QuestionState = {
  questions: [],
  totalQuestions: 0,
  isFetching: false,
  isPosting: false,
  isUpdating: false,
  isDeleting: false,
  dependentQuestions: [],
  isFetchingDependentQuestions: false,
};

export const getQuestions = createAsyncThunk(
  "question/getQuestions",
  async (params: GetQuestionParams) => {
    const response = await QuestionnaireService.getAllQuestions(params);
    return { response, pageNo: params.pageNo };
  },
);

// Powers the "Dependent Question" select in AddQuestionModal — kept separate
// from `questions`/`getQuestions` so opening the modal doesn't clobber the
// table's currently loaded list with this `isDependentEligible`-filtered one.
export const getDependentQuestions = createAsyncThunk(
  "question/getDependentQuestions",
  async (params: GetQuestionParams) => QuestionnaireService.getAllQuestions(params),
);

export const postQuestion = createAsyncThunk(
  "question/postQuestion",
  async (data: PostQuestionRequest) => QuestionnaireService.postQuestion(data),
);

export const updateQuestion = createAsyncThunk(
  "question/updateQuestion",
  async (params: { id: string; data: PostQuestionRequest }) =>
    QuestionnaireService.updateQuestion(params.id, params.data),
);

export const deleteQuestion = createAsyncThunk(
  "question/deleteQuestion",
  async (id: string) => QuestionnaireService.deleteQuestion(id),
);

// One bulk request with every question's new `displayOrder` — see
// services/api/questionnaire.ts's `updateQuestionsOrder`.
export const updateQuestionsOrder = createAsyncThunk(
  "question/updateQuestionsOrder",
  async (items: QuestionOrderItem[]) => QuestionnaireService.updateQuestionsOrder(items),
);

const questionSlice = createSlice({
  name: "question",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getQuestions.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getQuestions.fulfilled, (state, action) => {
        state.isFetching = false;
        const results = action.payload.response?.data?.results ?? [];
        const totalRecord = action.payload.response?.data?.totalRecord ?? 0;

        if (action.payload.pageNo > 1) {
          const existingIds = new Set(state.questions.map((question) => question.id));
          state.questions = [...state.questions, ...results.filter((question) => !existingIds.has(question.id))];
        } else {
          state.questions = results;
        }
        state.totalQuestions = totalRecord;
      })
      .addCase(getQuestions.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(getDependentQuestions.pending, (state) => {
        state.isFetchingDependentQuestions = true;
      })
      .addCase(getDependentQuestions.fulfilled, (state, action) => {
        state.isFetchingDependentQuestions = false;
        state.dependentQuestions = action.payload?.data?.results ?? [];
      })
      .addCase(getDependentQuestions.rejected, (state) => {
        state.isFetchingDependentQuestions = false;
      })
      .addCase(postQuestion.pending, (state) => {
        state.isPosting = true;
      })
      .addCase(postQuestion.fulfilled, (state) => {
        state.isPosting = false;
      })
      .addCase(postQuestion.rejected, (state) => {
        state.isPosting = false;
      })
      .addCase(updateQuestion.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateQuestion.fulfilled, (state) => {
        state.isUpdating = false;
      })
      .addCase(updateQuestion.rejected, (state) => {
        state.isUpdating = false;
      })
      .addCase(deleteQuestion.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteQuestion.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteQuestion.rejected, (state) => {
        state.isDeleting = false;
      });
  },
});

export default questionSlice.reducer;
